import { analyticsTree } from './analytics';
import {
  catchAllRoute,
  gettingReadyRoute,
  homeRoute,
  logoutRoute,
  noTeamRoute,
  partnersOverviewRedirectRoute,
  partnersRoute,
  settingsPasswordRoute,
  switchAccountRoute,
  teamRedirectRoute,
} from './auth/authed';
import {
  enterpriseRegisterRoute,
  loginRoute,
  partnerRegisterRoute,
  registerRoute,
} from './auth/guest';
import {
  forgottenPasswordRoute,
  registerInviteRoute,
  registerTeamMemberRoute,
  resetPasswordRoute,
  salesRegisterRoute,
  twoFactorChallengeRoute,
} from './auth/public';
import { configurationLeaves } from './configuration';
import { customersLeaves } from './customers';
import { formsLeaves } from './forms';
import { helpRoute } from './help';
import { integrationsLeaves } from './integrations';
import {
  authedChromeRoute,
  authedRoute,
  guestRoute,
  publicRoute,
  type RouterContext,
  rootRoute,
  siteRoute,
} from './layouts';
import { pricingLeaves } from './pricing';
import { settingsLeaves } from './settings';
import { envShortcutLeaves, siteShortcutLeaves } from './shortcuts';
import { siteLeaves, teamOverviewRoute } from './site';
import { staffTree } from './staff';
import { storefrontLeaves } from './storefront';

export type { RouterContext };

// Code-based route tree, assembled from one file per path under `src/routes/` (the file-based
// directory convention, hand-wired - no codegen, matching forms-automations).

const guestChildren = [loginRoute, registerRoute, partnerRegisterRoute, enterpriseRegisterRoute];
const guestTree = guestRoute.addChildren(guestChildren);

const siteChildren = [
  teamOverviewRoute,
  ...siteLeaves,
  ...configurationLeaves,
  ...customersLeaves,
  ...integrationsLeaves,
  ...storefrontLeaves,
  ...pricingLeaves,
  ...formsLeaves,
  analyticsTree,
];
const siteTree = siteRoute.addChildren(siteChildren);

// The authenticated pages that carry a `siteId` but no `siteEnv`: they share the sidebar/layout
// shell (`authedChromeRoute`) but resolve their own env. `/no-team`, `/getting-ready`,
// `/switch-account` and `/logout` have no site at all, so they stay bare under `authedRoute`.
const authedChromeChildren = [
  partnersRoute,
  partnersOverviewRedirectRoute,
  helpRoute,
  settingsPasswordRoute,
  ...settingsLeaves,
  staffTree,
];
const authedChromeTree = authedChromeRoute.addChildren(authedChromeChildren);

const authedChildren = [
  homeRoute,
  logoutRoute,
  noTeamRoute,
  gettingReadyRoute,
  switchAccountRoute,
  // The prefix-less aliases (`/pricing/lists` -> `/$siteId/$siteEnv/pricing/lists`). They are all
  // static-first, so they out-rank `teamRedirectRoute`'s single dynamic segment.
  ...envShortcutLeaves,
  ...siteShortcutLeaves,
  authedChromeTree,
  siteTree,
  // Last: `/$siteId` is one dynamic segment and must not shadow any static route above, and the
  // catch-all is lower still - a wildcard only wins where nothing else matched at all.
  teamRedirectRoute,
  catchAllRoute,
];
const authedTree = authedRoute.addChildren(authedChildren);

// Public pages hang off their own layout: no login needed, but a logged-in visitor still gets the
// session setup and its gates.
const publicTree = publicRoute.addChildren([
  twoFactorChallengeRoute,
  registerTeamMemberRoute,
  registerInviteRoute,
  forgottenPasswordRoute,
  resetPasswordRoute,
  salesRegisterRoute,
]);

export const routeTree = rootRoute.addChildren([guestTree, publicTree, authedTree]);
